/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {startTransition, useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {resetValue} from '@ion/dls/src/utils/ComponentUtils';
import {setFleetUpdateFlag} from '../../../store/actions/tracking';
import {trackingFleetUpdateAnyFetchingSelector} from '../../../store/selectors/tracking-selectors';

// safety cap for "wait for every registered consumer to settle" - well above
// the ~12s fill duration, so it only fires for a genuinely stuck/never-started
// fetch (one hung request, or every layer currently unmounted/filtered out so
// nothing ever registers) rather than letting either case freeze the panel's
// refresh cycle forever ...
const MAX_FETCH_WAIT_MS = 20000;

export interface Tracking2RefreshCycle {
  isRefreshFetching: boolean;
  updateCycleKey: number;
  onProgressCycleComplete: () => void;
}

/**
 * useTracking2RefreshCycle is the timing driver behind tracking2's "Data
 * Updates Every N Sec" side panel. `AssetListSidePanelHeader` fires
 * `onProgressCycleComplete` once its `AssetUpdateProgressBar` fill genuinely
 * completes; this hook pulses the shared `fleetUpdateFlag` every grid/map/
 * geofence/list consumer already reacts to via `useFleetUpdates`, then waits
 * on the shared `fleetUpdateFetchingKeys` registry those consumers report
 * into (rather than a local promise, since this hook doesn't own any of
 * their fetches) before letting the panel start its next cycle - with a
 * safety timeout in case a consumer never starts or never settles.
 *
 * ```typescript
 *
 *   import {useTracking2RefreshCycle} from
 *     '@/routes/tracking2/hooks/useTracking2RefreshCycle';
 *   ...
 *
 *   const {isRefreshFetching, updateCycleKey, onProgressCycleComplete} =
 *     useTracking2RefreshCycle();
 *
 * ```
 *
 * @returns isRefreshFetching       `true` while waiting on the shared registry
 * @returns updateCycleKey          bumped once per completed cycle - pass to
 *                                  `AssetUpdateProgressBar`'s `cycleKey` to
 *                                  remount (and so restart) its fill
 * @returns onProgressCycleComplete pass to `AssetUpdateProgressBar`'s
 *                                  `onCycleComplete`
 */
export const useTracking2RefreshCycle = (isListReloading = false): Tracking2RefreshCycle => {
  const dispatch = useDispatch();
  const isAnyFleetUpdateFetching = useSelector(trackingFleetUpdateAnyFetchingSelector);
  const [isRefreshFetching, setIsRefreshFetching] = useState(false);
  const [updateCycleKey, setUpdateCycleKey] = useState(0);
  const hasSeenFetchingRef = useRef(false);
  const cycleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // shared by the safety-timeout branch below and the settle effect - both
  // mark the exact moment every consumer's freshly settled data commits
  // (grid rows, map layers, sidesheet list all re-rendering together), so
  // both deprioritize it as a transition to keep the main thread responsive ...
  const settleCycle = useCallback(function() {
    startTransition(function() {
      setIsRefreshFetching(false);
      setUpdateCycleKey(key => key + 1);
    });
  }, []);

  // watches the shared registry while waiting on a cycle: requires seeing it
  // go true (at least one consumer actually started) before treating "false"
  // as "settled" - checking "not fetching" alone right after the pulse fires
  // would advance the cycle immediately, before any consumer's own effect has
  // even reacted to it yet ...
  const isListReloadingRef   = useRef(isListReloading);
  isListReloadingRef.current = isListReloading;

  useEffect(function() {
    if (isRefreshFetching) {
      if (isAnyFleetUpdateFetching) {
        hasSeenFetchingRef.current = true;
      } else if (hasSeenFetchingRef.current && !isListReloadingRef.current) {
        clearTimeout(cycleTimeoutRef.current);
        settleCycle();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshFetching, isAnyFleetUpdateFetching]);

  useEffect(function() {
    return function() {
      clearTimeout(cycleTimeoutRef.current);
    };
  }, []);

  // a list reload (a new sort, filter or group, or the first load) replaces
  // the data outright - the bar shows the fetch while it runs, then starts
  // its cycle over from zero, rather than carrying on from where it was ...
  const wasListReloadingRef = useRef(false);
  useEffect(function() {
    if (isListReloading) {
      clearTimeout(cycleTimeoutRef.current);
      // a finished fleet fetch from an earlier cycle must not settle this one ...
      hasSeenFetchingRef.current = false;
      setIsRefreshFetching(true);
    } else if (wasListReloadingRef.current) {
      settleCycle();
    }
    wasListReloadingRef.current = isListReloading;
  }, [isListReloading]);

  const onProgressCycleComplete = useCallback(function() {
    hasSeenFetchingRef.current = false;
    setIsRefreshFetching(true);
    // pulses true then back to false (matching the pulse the deleted
    // useFleetUpdateDriver used) - useFleetUpdates keys off the flag actually
    // *changing*, so leaving it at true would never re-fire on the next
    // cycle's dispatch of the same value. `fnCall` wraps its own dispatch in
    // startTransition rather than wrapping the outer `resetValue(...)` call -
    // resetValue invokes fnCall twice (the true value synchronously, the
    // false value later from its own internal setTimeout), so only wrapping
    // the outer call would leave that second, delayed dispatch unwrapped ...
    resetValue({
      initialValue : true,
      resetValue   : false,
      fnCall       : function(value: boolean) {
        startTransition(function() {
          dispatch(setFleetUpdateFlag(value));
        });
      }
    });
    clearTimeout(cycleTimeoutRef.current);
    cycleTimeoutRef.current = setTimeout(settleCycle, MAX_FETCH_WAIT_MS);
  }, [dispatch, settleCycle]);

  return {isRefreshFetching, updateCycleKey, onProgressCycleComplete};
};

export default useTracking2RefreshCycle;
